import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AdminLayout } from '@/components/layout/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  AlertCircle,
  FolderKanban,
  GitBranch,
  RefreshCw,
  Search,
  Clock,
  Loader2,
  CheckCircle,
} from 'lucide-react';
import { fetchAdminProjects, fetchBranchesForProject } from '@/api/admin';
import { toast } from 'sonner';

interface FetchStatus {
  projectId: number;
  status: 'pending' | 'success' | 'error';
  time: Date;
}

export function AdminProjectToolsPage() {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [fetchStatuses, setFetchStatuses] = useState<Record<number, FetchStatus>>({});

  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ['admin-project-tools'],
    queryFn: fetchAdminProjects,
    refetchInterval: autoRefresh ? 30000 : false,
  });

  const fetchMutation = useMutation({
    mutationFn: async (projectId: number) => {
      setFetchStatuses((prev) => ({
        ...prev,
        [projectId]: { projectId, status: 'pending', time: new Date() },
      }));
      return fetchBranchesForProject(projectId);
    },
    onSuccess: (_, projectId) => {
      setFetchStatuses((prev) => ({
        ...prev,
        [projectId]: { projectId, status: 'success', time: new Date() },
      }));
      toast.success('Branches fetch queued');
      queryClient.invalidateQueries({ queryKey: ['admin-project-tools'] });
    },
    onError: (err: any, projectId) => {
      setFetchStatuses((prev) => ({
        ...prev,
        [projectId]: { projectId, status: 'error', time: new Date() },
      }));
      toast.error(err.message || 'Failed to fetch branches');
    },
  });

  const projects = data || [];
  const filteredProjects = projects.filter((p) =>
    p.name.toLowerCase().includes(search.toLowerCase())
  );

  const pendingCount = Object.values(fetchStatuses).filter((s) => s.status === 'pending').length;

  const handleFetchAll = () => {
    if (confirm(`Fetch branches for all ${filteredProjects.length} projects? This may take a while.`)) {
      filteredProjects.forEach((project) => {
        fetchMutation.mutate(project.id);
      });
    }
  };

  const getStatusBadge = (projectId: number) => {
    const status = fetchStatuses[projectId];
    if (!status) return null;

    if (status.status === 'pending') {
      return (
        <Badge className="bg-blue-100 text-blue-800 hover:bg-blue-100">
          <Loader2 className="h-3 w-3 mr-1 animate-spin" />
          Fetching...
        </Badge>
      );
    }
    if (status.status === 'success') {
      return (
        <Badge className="bg-green-100 text-green-800 hover:bg-green-100">
          <CheckCircle className="h-3 w-3 mr-1" />
          Queued
        </Badge>
      );
    }
    if (status.status === 'error') {
      return (
        <Badge variant="destructive">
          <AlertCircle className="h-3 w-3 mr-1" />
          Failed
        </Badge>
      );
    }
    return null;
  };

  return (
    <AdminLayout>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold">Project Tools</h1>
          <p className="text-sm text-muted-foreground">Manual project maintenance actions</p>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Switch
              id="auto-refresh"
              checked={autoRefresh}
              onCheckedChange={setAutoRefresh}
            />
            <Label htmlFor="auto-refresh" className="text-sm">Auto-refresh</Label>
          </div>
          <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isFetching}>
            <RefreshCw className={`h-4 w-4 mr-2 ${isFetching ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <FolderKanban className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Projects</p>
                <p className="text-2xl font-semibold">{projects.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <Search className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Showing</p>
                <p className="text-2xl font-semibold">{filteredProjects.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <GitBranch className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Pending Fetches</p>
                <p className="text-2xl font-semibold">{pendingCount}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <Clock className="h-5 w-5 text-yellow-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Status</p>
                <p className="text-lg font-semibold">
                  {fetchMutation.isPending ? 'Working...' : 'Ready'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>Failed to load projects.</AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <FolderKanban className="h-5 w-5" />
              Projects
            </CardTitle>
            <div className="flex items-center gap-2">
              <Input
                placeholder="Search projects..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-64"
              />
              <Button
                variant="outline"
                onClick={handleFetchAll}
                disabled={fetchMutation.isPending || filteredProjects.length === 0}
              >
                <GitBranch className="h-4 w-4 mr-2" />
                Fetch All ({filteredProjects.length})
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : filteredProjects.length ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-muted-foreground border-b">
                    <th className="py-3 px-2 font-medium">ID</th>
                    <th className="py-3 px-2 font-medium">Project Name</th>
                    <th className="py-3 px-2 font-medium">Status</th>
                    <th className="py-3 px-2 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredProjects.map((project) => (
                    <tr key={project.id} className="border-b hover:bg-muted/50">
                      <td className="py-3 px-2">
                        <code className="text-xs">#{project.id}</code>
                      </td>
                      <td className="py-3 px-2">
                        <span className="font-medium">{project.name}</span>
                      </td>
                      <td className="py-3 px-2">
                        {getStatusBadge(project.id) || (
                          <Badge variant="outline">Ready</Badge>
                        )}
                      </td>
                      <td className="py-3 px-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => fetchMutation.mutate(project.id)}
                          disabled={fetchStatuses[project.id]?.status === 'pending'}
                        >
                          <GitBranch className="h-4 w-4 mr-2" />
                          Fetch Branches
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-12 text-muted-foreground">
              <FolderKanban className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>{search ? 'No projects match your search' : 'No projects found'}</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Progress Summary */}
      {Object.keys(fetchStatuses).length > 0 && (
        <Card className="mt-6">
          <CardHeader>
            <CardTitle className="text-sm font-medium">Recent Fetch Activity</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {Object.values(fetchStatuses)
                .sort((a, b) => b.time.getTime() - a.time.getTime())
                .slice(0, 10)
                .map((status) => {
                  const project = projects.find((p) => p.id === status.projectId);
                  return (
                    <div
                      key={status.projectId}
                      className="flex items-center justify-between text-sm p-2 rounded bg-muted/50"
                    >
                      <div className="flex items-center gap-2">
                        {status.status === 'pending' && <Loader2 className="h-4 w-4 animate-spin text-blue-600" />}
                        {status.status === 'success' && <CheckCircle className="h-4 w-4 text-green-600" />}
                        {status.status === 'error' && <AlertCircle className="h-4 w-4 text-red-600" />}
                        <span>{project?.name || `Project #${status.projectId}`}</span>
                      </div>
                      <span className="text-xs text-muted-foreground">
                        {status.time.toLocaleTimeString()}
                      </span>
                    </div>
                  );
                })}
            </div>
          </CardContent>
        </Card>
      )}
    </AdminLayout>
  );
}
